<template>
  <div class="productIssue">
    <card-m
      :title="title"
      cardWidth="100%"
      cardHeight="100%"
      maxHeight="unset"
      toolWidth="70%"
      toolJustifyontent="flex-start"
      :show-filter="false"
      :show-export="true"
      :show-column-edit="true"
      export-file-name="产品问题列表"
      @export="handleExport"
      @edit-column="openColumnEdit"
    >
      <!-- 工具区：返回产品主页 -->
      <template slot="card-tool">
        <el-button size="mini" type="primary" plain @click="backToProducts">返回产品主页</el-button>
      </template>

      <!-- 表格 -->
      <template slot="card-box">
        <card-table
          tableID="productIssueTable"
          :key="tableRenderKey"
          :total="totalCounts"
          :currentPage="currentPage"
          @sizeChange="handleSizeChanges"
          @currentChange="handleCurrentChanges"
          :tableColumn="effectiveVisibleColumns"
          :tableData="issueData"
          style="height: 100%;"
          :showIndex="true"
          :loading="loading"
          :showPaging="true"
          :size="pageSize"
          :mobileColumns="mobileColumnEditFilter"
          @column-filter-change="onColumnFilterChange"
          :columnFilterValues="columnFilterValues"
          @column-width-change="onColumnWidthChange"
        >
        </card-table>
      </template>
    </card-m>

    <!-- 列编辑弹窗 -->
    <column-edit-dialog
      :visible.sync="columnEditVisible"
      :columns="tempOrderedColumns"
      :defaultColumns="effectiveDefaultColumnDefs"
      :title="columnEditTitle"
      :dialogWidth="columnEditDialogWidth"
      @confirm="handleColumnSave"
      @cancel="handleColumnCancel"
    />
  </div>
</template>

<script>
import { getProductIssueList } from '@/api/mainapi.js';
import columnEditMixin from '@/mixins/columnEditMixin';
import ColumnEditDialog from '@/components/ColumnEditDialog.vue';
import FileSaver from 'file-saver';
import XLSX from 'xlsx';

/* 产品状态枚举：与 productProgress 保持一致（process_status / execute_status 共用） */
const PRODUCT_STATUS_ENUM = {
  1: '下达',
  2: '装配中',
  3: '调试中',
  4: '检验中',
  5: '出库',
  6: '正常',
  7: '暂停',
  8: '停工',
  9: '入库中',
  10: '配料',
  11: '入库',
  12: '领料',
  13: '点料'
};
const PROCESS_STATUS_KEYS = [1, 2, 3, 4, 5, 9, 10, 11, 12, 13];
const EXECUTE_STATUS_KEYS = [1, 6, 7, 8];
const toStatusOptions = keys => [
  { label: '全部', value: '' },
  ...keys.map(key => ({ label: PRODUCT_STATUS_ENUM[key], value: String(key) }))
];
const PRODUCT_PROCESS_OPTIONS = toStatusOptions(PROCESS_STATUS_KEYS);
const PRODUCT_EXECUTE_OPTIONS = toStatusOptions(EXECUTE_STATUS_KEYS);

export default {
  name: 'productIssueProgress',
  mixins: [columnEditMixin],
  components: { ColumnEditDialog },
  props: {
    title: {
      type: String,
      default: '产品问题信息'
    }
  },
  data() {
    return {
      // 列偏好页面标识（mixin 需要）
      pageType: 'productIssueProgress',

      // 表格列定义（后端字段为 snake_case，这里统一转成小驼峰展示字段）
      issueColumns: [
        { prop: 'productId', label: '产品ID', minWidth: 80 },
        { prop: 'serialCode', label: '产品编号', filterable: true, minWidth: 130 },
        { prop: 'fsn', label: '短代码', filterable: true, minWidth: 90 },
        {
          prop: 'name',
          label: '产品名称',
          filterable: true,
          // 附加「不包含」关键字筛选（对应后端 nameNot）
          notFilterable: true,
          filterPlaceholder: '输入包含关键字',
          notFilterPlaceholder: '输入不包含关键字',
          minWidth: 220
        },
        { prop: 'model', label: '产品型号', filterable: true, minWidth: 140 },
        { prop: 'taskNo', label: '任务单号', filterable: true, minWidth: 130 },
        { prop: 'operationPersonName', label: '问题人员', filterable: true},
        { prop: 'issueType', label: '问题类型', filterable: true },
        { prop: 'issueDescribe', label: '问题', filterable: true},
        { prop: 'issueDetail', label: '问题备注', filterable: true },
        // 问题创建时间：范围筛选 → issueCreateTimeStart / issueCreateTimeEnd
        { prop: 'issueCreateTime', label: '问题创建时间', filterable: true, filterType: 'date'},        
        { prop: 'productionDepartment', label: '生产车间', filterable: true, minWidth: 130 },
        {
          prop: 'processStatusText',
          label: '产品进程',
          filterable: true,
          filterType: 'select',
          filterOptions: PRODUCT_PROCESS_OPTIONS,
          minWidth: 100
        },
        {
          prop: 'executeStatusText',
          label: '产品执行状态',
          filterable: true,
          filterType: 'select',
          filterOptions: PRODUCT_EXECUTE_OPTIONS,
          minWidth: 110
        },
        // 产品创建时间：范围筛选 → productCreateTimeStart / productCreateTimeEnd
        { prop: 'productCreateTime', label: '创建时间', filterable: true, filterType: 'date' },
        { prop: 'parameter', label: '调试参数', filterable: true },
        { prop: 'productDetail', label: '备注', filterable: true},

      ],

      // 手机端默认显示列
      mobileColumnFilter: [
        'serialCode',
        'fsn',
        'name',
        'model',
        'processStatusText',
        'executeStatusText',
        'issueType',
        'issueDescribe'
      ],
      // 手机端列编辑弹窗中展示的列
      mobileColumnEditFilter: [
        'productId',
        'serialCode',
        'fsn',
        'name',
        'model',
        'taskNo',
        'productionDepartment',
        'processStatusText',
        'executeStatusText',
        'productCreateTime',
        'parameter',
        'productDetail',
        'operationPersonName',
        'issueType',
        'issueDescribe',
        'issueDetail',
        'issueCreateTime'
      ],

      // 列筛选关键词（key 与后端入参一一对应）
      serialCodeKeyword: '',
      fsnKeyword: '',
      nameKeyword: '',
      nameNotKeyword: '',          // 产品名称「不包含」关键字 → nameNot
      modelKeyword: '',
      taskNoKeyword: '',
      productionDepartmentKeyword: '',
      processStatusKeyword: '',    // 产品进程（枚举数字字符串）
      executeStatusKeyword: '',    // 产品执行状态（枚举数字字符串）
      productCreateTimeStart: '',  // 创建时间起
      productCreateTimeEnd: '',    // 创建时间止
      parameterKeyword: '',
      productDetailKeyword: '',    // 备注（产品备注）
      operationPersonNameKeyword: '',
      issueTypeKeyword: '',
      issueDescribeKeyword: '',
      issueDetailKeyword: '',      // 问题备注
      issueCreateTimeStart: '',    // 问题创建时间起
      issueCreateTimeEnd: '',      // 问题创建时间止
      columnFilterValues: {},

      // 分页
      currentPage: 1,
      pageSize: 50,

      // 数据
      issueData: [],
      totalCounts: 0,
      loading: false
    };
  },
  computed: {
    // 供 mixin 使用的列定义
    baseColumnDefs() {
      return this.issueColumns;
    }
  },
  async mounted() {
    await this.loadColumnPref();
    this.getIssueList();
  },
  methods: {
    /** 返回产品主页 */
    backToProducts() {
      this.$router.push('/homepage/products');
    },

    /** 获取产品问题列表 */
    async getIssueList() {
      this.loading = true;

      const params = {
        pageNo: String(this.currentPage),
        pageSize: String(this.pageSize),
        serialCode: this.serialCodeKeyword || '',
        fsn: this.fsnKeyword || '',
        name: this.nameKeyword || '',
        nameNot: this.nameNotKeyword || '',
        model: this.modelKeyword || '',
        taskNo: this.taskNoKeyword || '',
        processStatus: this.processStatusKeyword || '',
        executeStatus: this.executeStatusKeyword || '',
        productCreateTimeStart: this.productCreateTimeStart || '',
        productCreateTimeEnd: this.productCreateTimeEnd || '',
        parameter: this.parameterKeyword || '',
        productionDepartment: this.productionDepartmentKeyword || '',
        issueCreateTimeStart: this.issueCreateTimeStart || '',
        issueCreateTimeEnd: this.issueCreateTimeEnd || '',
        issueUpdateTime: '',
        issueType: this.issueTypeKeyword || '',
        issueDescribe: this.issueDescribeKeyword || '',
        operationSource: '',
        operationPersonName: this.operationPersonNameKeyword || '',
        detail: this.issueDetailKeyword || '',          // 问题备注
        productDetail: this.productDetailKeyword || '', // 产品备注
        range: ''
      };

      try {
        const res = await getProductIssueList(params);
        this.loading = false;

        if (res.data.code === 200) {
          const data = res.data.data || {};
          this.issueData = this.processIssueData(data.list || []);
          this.totalCounts = data.total || data.totalCount || data.totalCounts || 0;
        } else {
          this.$message.error(res.data.message || '获取产品问题列表失败');
        }
      } catch (error) {
        this.loading = false;
        this.$message.error('网络错误，请检查连接');
      }
    },

    /** 处理产品问题数据：后端 snake_case → 表格展示字段 */
    processIssueData(list) {
      if (!Array.isArray(list)) return [];
      return list.map(item => ({
        productId: item.id === undefined || item.id === null ? '' : item.id,
        serialCode: item.serial_code || '',
        fsn: item.fsn || '',
        name: item.name || '',
        model: item.model || '',
        taskNo: item.task_number || '',
        productionDepartment: item.productionDepartment || '',
        processStatusText: PRODUCT_STATUS_ENUM[item.process_status] || '',
        executeStatusText: PRODUCT_STATUS_ENUM[item.execute_status] || '',
        productCreateTime: this.formatDate(item.productCreateTime || item.create_time),
        parameter: item.parameter || '',
        productDetail: item.productDetail || item.detail || '',
        operationPersonName: item.operation_person_name || '',
        issueType: item.issue_type || '',
        issueDescribe: item.issue_describe || '',
        issueDetail: item.issueDetail || '',
        issueCreateTime: this.formatDate(item.issueCreateTime),
        _raw: item
      }));
    },

    /** 列筛选变化 */
    onColumnFilterChange(filterValues) {
      this.serialCodeKeyword = filterValues['serialCode'] || '';
      this.fsnKeyword = filterValues['fsn'] || '';
      this.nameKeyword = filterValues['name'] || '';
      // 产品名称「不包含」关键字：card-table 存于 filterValues['name__not']
      this.nameNotKeyword = filterValues['name__not'] || '';
      this.modelKeyword = filterValues['model'] || '';
      this.taskNoKeyword = filterValues['taskNo'] || '';
      this.productionDepartmentKeyword = filterValues['productionDepartment'] || '';
      this.processStatusKeyword = filterValues['processStatusText'] || '';
      this.executeStatusKeyword = filterValues['executeStatusText'] || '';
      this.parameterKeyword = filterValues['parameter'] || '';
      this.productDetailKeyword = filterValues['productDetail'] || '';
      this.operationPersonNameKeyword = filterValues['operationPersonName'] || '';
      this.issueTypeKeyword = filterValues['issueType'] || '';
      this.issueDescribeKeyword = filterValues['issueDescribe'] || '';
      this.issueDetailKeyword = filterValues['issueDetail'] || '';

      // filterType: 'date' 的取值是 [开始日期, 结束日期]
      const productRange = Array.isArray(filterValues['productCreateTime']) ? filterValues['productCreateTime'] : [];
      this.productCreateTimeStart = productRange[0] || '';
      this.productCreateTimeEnd = productRange[1] || '';
      const issueRange = Array.isArray(filterValues['issueCreateTime']) ? filterValues['issueCreateTime'] : [];
      this.issueCreateTimeStart = issueRange[0] || '';
      this.issueCreateTimeEnd = issueRange[1] || '';

      this.columnFilterValues = filterValues;
      this.currentPage = 1;
      this.tableRenderKey++;
      this.getIssueList();
    },

    /** 改变每页条数 */
    handleSizeChanges(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.tableRenderKey++;
      this.getIssueList();
    },

    /** 改变页码 */
    handleCurrentChanges(val) {
      this.currentPage = val;
      this.getIssueList();
    },

    /** 导出Excel */
    handleExport() {
      const xlsxParam = { raw: true };
      const wb = XLSX.utils.table_to_book(
        document.querySelector('#productIssueTable'),
        xlsxParam
      );
      const wbout = XLSX.write(wb, {
        bookType: 'xlsx',
        bookSST: true,
        type: 'array'
      });
      try {
        FileSaver.saveAs(
          new Blob([wbout], { type: 'application/octet-stream' }),
          '产品问题列表.xlsx'
        );
      } catch (e) {
        // 导出失败时静默处理，避免在无 console 环境报错
      }
      return wbout;
    },

    /** 日期格式化：取前 10 位（yyyy-MM-dd） */
    formatDate(val) {
      if (!val) return '';
      const str = String(val);
      return str.length >= 10 ? str.substring(0, 10) : str;
    }
  }
};
</script>

<style scoped lang="scss">
.productIssue {
  width: 100%;
  height: calc(100vh - 65px);
  display: flex;
  flex-direction: column;
}

::v-deep .card-m {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

::v-deep .card-box {
  flex: 1;
  min-height: 0;
  overflow-y: hidden;
  overflow-x: hidden;
}

/* 手机端 */
@media (max-width: 768px) {
  .productIssue {
    height: calc(100vh - 50px);
  }
}
</style>
